import { api } from '@/lib/api';
import { unwrapApiData } from '@/lib/store-formation';

export type FunnelStepType = 'capture' | 'sales' | 'checkout' | 'thank_you' | 'custom';
export type FunnelStatus = 'draft' | 'published';
export type FunnelType = 'simple' | 'formation' | 'physical_product' | 'bundle';
export type FunnelTrackEvent = 'capture_view' | 'sales_view' | 'checkout_view' | 'lead' | 'purchase';

export interface FunnelStats {
  captureViews?: number;
  salesViews?: number;
  checkoutViews?: number;
  leads?: number;
  purchases?: number;
  ab?: Array<{
    variant: 'a' | 'b';
    captureViews: number;
    salesViews: number;
    checkoutViews: number;
    purchases: number;
    conversionLeadPct: number;
    conversionSalePct: number;
    conversionCheckoutPct: number;
  }> | null;
}

export interface FunnelHeroContent {
  title?: string;
  subtitle?: string;
  coverImageUrl?: string | null;
  ctaText?: string;
}

export interface FunnelFeatureItem {
  title: string;
  description: string;
}

export interface FunnelFaqItem {
  question: string;
  answer: string;
}

export interface FunnelSection {
  id: string;
  type: 'features' | 'faq';
  title?: string;
  items?: FunnelFeatureItem[];
  questions?: FunnelFaqItem[];
}

export interface FunnelCaptureContent {
  hero?: FunnelHeroContent;
  form?: {
    showPhone?: boolean;
    consentLabel?: string;
  };
}

export interface FunnelSalesContent {
  hero?: FunnelHeroContent;
  sections?: FunnelSection[];
  countdown?: {
    enabled?: boolean;
    endsAt?: string;
    label?: string;
  };
  upsell?: {
    enabled?: boolean;
    targetFunnelSlug?: string;
    headline?: string;
    subtitle?: string;
    ctaText?: string;
  };
  downsell?: {
    enabled?: boolean;
    targetFunnelSlug?: string;
    headline?: string;
    subtitle?: string;
    ctaText?: string;
  };
}

export interface FunnelUpsellOffer {
  slug: string;
  formationId?: string | null;
  title: string;
  description?: string | null;
  price: number;
  coverImageUrl?: string | null;
  headline: string;
  subtitle?: string | null;
  ctaText: string;
}

export interface PublicSalesFunnel extends SalesFunnel {
  orderBump?: unknown;
  upsellOffer?: FunnelUpsellOffer | null;
  downsellOffer?: FunnelUpsellOffer | null;
}

export interface FunnelStep {
  id: string;
  type: FunnelStepType;
  slug: string;
  order: number;
  title: string;
  content: FunnelSalesContent | Record<string, unknown>;
}

export interface FunnelStoreFormation {
  id: string;
  title: string;
  description?: string | null;
  price: number;
  coverImageUrl?: string | null;
  flowId: string;
  category?: string | null;
  status?: string;
  isPublished?: boolean;
}

export interface FunnelPhysicalProduct {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number;
  stock?: number;
  shipping?: unknown;
  variants?: unknown;
  isActive?: boolean;
}

export interface FunnelSimpleOffer {
  title?: string;
  description?: string | null;
  price?: number;
  coverImageUrl?: string | null;
}

export interface ResolvedFunnelOffer {
  kind: FunnelType;
  title: string;
  description?: string | null;
  price: number;
  coverImageUrl?: string | null;
  category?: string | null;
  formationId?: string;
  flowId?: string;
  creationMode?: 'simple' | 'advanced';
  shortCodePublic?: string | null;
  productId?: string;
}

export interface FunnelOrderBumpProduct {
  kind: 'physical' | 'formation';
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number;
  stock?: number;
  variants?: unknown;
  shipping?: { mode?: string; amount?: number; freeAbove?: number | null };
}

export interface SalesFunnel {
  id: string;
  name: string;
  slug: string;
  type: FunnelType;
  status: FunnelStatus;
  currency: string;
  settings?: import('@/lib/funnel-objective').FunnelSettings | Record<string, unknown>;
  offer?: FunnelSimpleOffer | Record<string, unknown>;
  resolvedOffer?: ResolvedFunnelOffer;
  stats?: FunnelStats;
  storeFormation?: FunnelStoreFormation | null;
  physicalProduct?: FunnelPhysicalProduct | null;
  storeFormationBundle?: {
    id: string;
    title: string;
    description?: string | null;
    coverImageUrl?: string | null;
    price: number;
    items?: Array<{ formation: { id: string; title: string; price: number; coverImageUrl?: string | null; flowId?: string } }>;
  } | null;
  steps: FunnelStep[];
  orderBump?: FunnelOrderBumpProduct | null;
  orderBumps?: FunnelOrderBumpProduct[];
  upsellOffer?: FunnelUpsellOffer | null;
  downsellOffer?: FunnelUpsellOffer | null;
  storeFormations?: import('@/lib/lyrraone-public').LoPublicFormationCard[];
  storeBundles?: import('@/lib/lyrraone-public').LoPublicFormationBundleCard[];
  creatorSiteSlug?: string | null;
  user?: { firstName?: string | null; lastName?: string | null };
  createdAt?: string;
  updatedAt?: string;
}

export interface AvailableFormationForFunnel {
  id: string;
  title: string;
  price: number;
  coverImageUrl?: string | null;
  flowId: string;
  creationMode?: 'simple' | 'advanced';
  shortCodePublic?: string | null;
  funnelSlug?: string | null;
}

export async function fetchPublicFunnel(slug: string): Promise<SalesFunnel | null> {
  try {
    const resp = await api.get(`/public/funnels/${encodeURIComponent(slug)}`);
    return unwrapApiData<SalesFunnel>(resp);
  } catch {
    return null;
  }
}

export async function fetchMyFunnels(): Promise<SalesFunnel[]> {
  const resp = await api.get('/funnels');
  return unwrapApiData<SalesFunnel[]>(resp);
}

export async function fetchFunnel(id: string): Promise<SalesFunnel> {
  const resp = await api.get(`/funnels/${id}`);
  return unwrapApiData<SalesFunnel>(resp);
}

export async function fetchAvailableFormations(): Promise<AvailableFormationForFunnel[]> {
  const resp = await api.get('/funnels/available-formations');
  return unwrapApiData<AvailableFormationForFunnel[]>(resp);
}

export async function fetchFunnelLinkFormations(): Promise<AvailableFormationForFunnel[]> {
  const resp = await api.get('/funnels/link-formations');
  return unwrapApiData<AvailableFormationForFunnel[]>(resp);
}

export interface AvailablePhysicalProductForFunnel {
  id: string;
  name: string;
  price: number;
  imageUrl?: string | null;
  stock: number;
}

export async function fetchAvailablePhysicalProducts(): Promise<AvailablePhysicalProductForFunnel[]> {
  const resp = await api.get('/funnels/available-physical-products');
  return unwrapApiData<AvailablePhysicalProductForFunnel[]>(resp);
}

export async function createFunnel(payload: {
  type: FunnelType;
  name?: string;
  storeFormationId?: string;
  physicalProductId?: string;
  storeFormationBundleId?: string;
  offer?: FunnelSimpleOffer;
  templateId?: string;
  currency?: string;
  settings?: Record<string, unknown>;
}): Promise<SalesFunnel> {
  const resp = await api.post('/funnels', payload);
  return unwrapApiData<SalesFunnel>(resp);
}

export async function createFunnelFromFormation(storeFormationId: string): Promise<SalesFunnel> {
  return createFunnel({ type: 'formation', storeFormationId });
}

export async function updateFunnel(
  funnelId: string,
  data: {
    name?: string;
    currency?: string;
    settings?: Record<string, unknown>;
    offer?: FunnelSimpleOffer;
  },
): Promise<SalesFunnel> {
  const resp = await api.patch(`/funnels/${funnelId}`, data);
  return unwrapApiData<SalesFunnel>(resp);
}

export async function duplicateFunnel(funnelId: string): Promise<SalesFunnel> {
  const resp = await api.post(`/funnels/${funnelId}/duplicate`);
  return unwrapApiData<SalesFunnel>(resp);
}

export async function deleteFunnel(funnelId: string): Promise<{ deleted: boolean }> {
  const resp = await api.delete(`/funnels/${funnelId}`);
  return unwrapApiData<{ deleted: boolean }>(resp);
}

export async function purchaseFunnel(
  slug: string,
  data?: {
    affiliateCode?: string;
    shippingAddress?: {
      fullName: string;
      line1: string;
      line2?: string;
      city: string;
      postalCode: string;
      country?: string;
    };
    orderBump?: {
      productId: string;
      quantity?: number;
      shippingAddress: {
        fullName: string;
        line1: string;
        line2?: string;
        city: string;
        postalCode: string;
        country?: string;
      };
    };
  },
): Promise<unknown> {
  const resp = await api.post(`/public/funnels/${encodeURIComponent(slug)}/purchase`, data ?? {});
  return unwrapApiData(resp);
}

export async function updateFunnelStep(
  funnelId: string,
  stepId: string,
  data: { title?: string; content?: Record<string, unknown> },
): Promise<FunnelStep> {
  const resp = await api.patch(`/funnels/${funnelId}/steps/${stepId}`, data);
  return unwrapApiData<FunnelStep>(resp);
}

export async function addFunnelStep(
  funnelId: string,
  type: FunnelStepType,
  options?: { title?: string },
): Promise<SalesFunnel> {
  const resp = await api.post(`/funnels/${funnelId}/steps`, { type, title: options?.title });
  return unwrapApiData<SalesFunnel>(resp);
}

export async function deleteFunnelStep(funnelId: string, stepId: string): Promise<SalesFunnel> {
  const resp = await api.delete(`/funnels/${funnelId}/steps/${stepId}`);
  return unwrapApiData<SalesFunnel>(resp);
}

export async function reorderFunnelSteps(funnelId: string, stepIds: string[]): Promise<SalesFunnel> {
  const resp = await api.put(`/funnels/${funnelId}/steps/reorder`, { stepIds });
  return unwrapApiData<SalesFunnel>(resp);
}

export function getSortedSteps(funnel: SalesFunnel): FunnelStep[] {
  return [...funnel.steps].sort((a, b) => a.order - b.order);
}

export function getStepById(funnel: SalesFunnel, stepId: string): FunnelStep | undefined {
  return funnel.steps.find((s) => s.id === stepId);
}

export async function setFunnelStatus(funnelId: string, status: FunnelStatus): Promise<SalesFunnel> {
  const resp = await api.patch(`/funnels/${funnelId}/status`, { status });
  return unwrapApiData<SalesFunnel>(resp);
}

export interface FunnelAnalytics {
  funnel: {
    id: string;
    name: string;
    slug: string;
    status: FunnelStatus;
    formation: { id: string; title: string; price: number; salesCount?: number };
  };
  stats: FunnelStats & {
    conversionLeadPct: number;
    conversionSalePct: number;
  };
}

export async function fetchFunnelAnalytics(funnelId: string): Promise<FunnelAnalytics> {
  const resp = await api.get(`/funnels/${funnelId}/analytics`);
  return unwrapApiData<FunnelAnalytics>(resp);
}

export interface FunnelTemplateHints {
  hasFeatures: boolean;
  hasFaq: boolean;
  featureCount: number;
  faqCount: number;
  hasCountdown: boolean;
  hasPhoneCapture: boolean;
}

export interface FunnelTemplateMeta {
  id: string;
  name: string;
  description: string;
  hints?: FunnelTemplateHints;
}

export interface FunnelTemplatePreview {
  templateId: string;
  name: string;
  description: string;
  hints: FunnelTemplateHints;
  offer: {
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    price: number;
  };
  steps: {
    capture: FunnelCaptureContent;
    sales: FunnelSalesContent;
    checkout: { headline?: string };
    thank_you: { hero?: FunnelHeroContent };
  };
}

export async function fetchFunnelTemplates(): Promise<FunnelTemplateMeta[]> {
  const resp = await api.get('/funnels/templates');
  return unwrapApiData<FunnelTemplateMeta[]>(resp);
}

export async function fetchFunnelTemplatePreview(
  templateId: string,
  offer: { title: string; description?: string | null; price?: number; coverImageUrl?: string | null },
): Promise<FunnelTemplatePreview> {
  const params = new URLSearchParams();
  params.set('title', offer.title);
  if (offer.description) params.set('description', offer.description);
  if (offer.price != null) params.set('price', String(offer.price));
  if (offer.coverImageUrl) params.set('coverImageUrl', offer.coverImageUrl);
  const resp = await api.get(`/funnels/templates/${encodeURIComponent(templateId)}/preview?${params}`);
  return unwrapApiData<FunnelTemplatePreview>(resp);
}

export interface FunnelStepLayoutMeta {
  id: string;
  stepType: FunnelStepType;
  name: string;
  description: string;
  hints?: {
    hasCountdown?: boolean;
    hasFeatures?: boolean;
    hasFaq?: boolean;
    hasPhoneCapture?: boolean;
    columns?: 1 | 2;
    accent?: string;
  };
}

export async function fetchFunnelStepLayouts(stepType: FunnelStepType): Promise<FunnelStepLayoutMeta[]> {
  const resp = await api.get(`/funnels/step-layouts?stepType=${encodeURIComponent(stepType)}`);
  return unwrapApiData<FunnelStepLayoutMeta[]>(resp);
}

export async function applyFunnelStepLayout(
  funnelId: string,
  stepId: string,
  layoutId: string,
): Promise<SalesFunnel> {
  const resp = await api.post(`/funnels/${funnelId}/steps/${stepId}/apply-layout`, { layoutId });
  return unwrapApiData<SalesFunnel>(resp);
}

export async function applyFunnelStepTemplate(
  funnelId: string,
  stepId: string,
  templateId: string,
): Promise<SalesFunnel> {
  const resp = await api.post(`/funnels/${funnelId}/steps/${stepId}/apply-template`, { templateId });
  return unwrapApiData<SalesFunnel>(resp);
}

export async function fetchFunnelAutomationMeta(stepType: FunnelStepType): Promise<import('@/lib/funnel-automation').FunnelAutomationMeta> {
  const resp = await api.get(`/funnels/automation-meta?stepType=${encodeURIComponent(stepType)}`);
  return unwrapApiData(resp);
}

export async function applyFunnelTemplate(funnelId: string, templateId: string): Promise<SalesFunnel> {
  const resp = await api.post(`/funnels/${funnelId}/apply-template`, { templateId });
  return unwrapApiData<SalesFunnel>(resp);
}

export interface FunnelUpsellTarget {
  slug: string;
  name: string;
  type?: FunnelType;
  offer?: FunnelSimpleOffer | Record<string, unknown>;
  storeFormation?: {
    title: string;
    price: number;
    coverImageUrl?: string | null;
    description?: string | null;
  } | null;
  physicalProduct?: {
    name: string;
    price: number;
    imageUrl?: string | null;
    description?: string | null;
  } | null;
}

export function upsellTargetDisplay(t: FunnelUpsellTarget): { title: string; price: number } {
  if (t.storeFormation) return { title: t.storeFormation.title, price: t.storeFormation.price };
  if (t.physicalProduct) return { title: t.physicalProduct.name, price: t.physicalProduct.price };
  const o = t.offer && typeof t.offer === 'object' ? t.offer as FunnelSimpleOffer : {};
  return { title: o.title ?? t.name, price: o.price ?? 0 };
}

export async function fetchFunnelUpsellTargets(funnelId: string): Promise<FunnelUpsellTarget[]> {
  const resp = await api.get(`/funnels/${funnelId}/upsell-targets`);
  return unwrapApiData<FunnelUpsellTarget[]>(resp);
}

export function funnelPublicUrl(slug: string, step?: 'capture' | 'vente' | 'checkout' | 'merci'): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  if (step === 'capture') return `${base}/f/${encodeURIComponent(slug)}/capture`;
  if (step === 'vente') return `${base}/f/${encodeURIComponent(slug)}/vente`;
  if (step === 'checkout') return `${base}/f/${encodeURIComponent(slug)}/checkout`;
  if (step === 'merci') return `${base}/f/${encodeURIComponent(slug)}/merci`;
  return `${base}/f/${encodeURIComponent(slug)}`;
}

export type FunnelStepRef = FunnelStepType | Pick<FunnelStep, 'type' | 'slug'>;

/** Chemin public d'une étape (sans origine). */
export function funnelStepPublicPath(funnelSlug: string, step: FunnelStepRef): string {
  const encodedFunnel = encodeURIComponent(funnelSlug);
  if (typeof step === 'object') {
    if (step.type === 'custom') {
      return `/f/${encodedFunnel}/${encodeURIComponent(step.slug)}`;
    }
    step = step.type;
  }
  switch (step) {
    case 'capture':
      return `/f/${encodedFunnel}/capture`;
    case 'sales':
      return `/f/${encodedFunnel}/vente`;
    case 'checkout':
      return `/f/${encodedFunnel}/checkout`;
    case 'thank_you':
      return `/f/${encodedFunnel}/merci`;
    default:
      return `/f/${encodedFunnel}`;
  }
}

/** URL publique d'une étape funnel (avec origine si disponible). */
export function funnelStepPublicUrl(slug: string, step: FunnelStepRef): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}${funnelStepPublicPath(slug, step)}`;
}

/** Étape suivante dans le tunnel (ordre éditeur). */
export function getNextFunnelStep(
  funnel: Pick<SalesFunnel, 'steps'>,
  currentStep: Pick<FunnelStep, 'id' | 'type' | 'slug' | 'order'>,
): FunnelStep | undefined {
  const sorted = getSortedSteps(funnel as SalesFunnel);
  const idx = sorted.findIndex((s) => s.id === currentStep.id);
  const resolvedIdx = idx >= 0
    ? idx
    : sorted.findIndex((s) => s.slug === currentStep.slug && s.type === currentStep.type);
  if (resolvedIdx < 0 || resolvedIdx >= sorted.length - 1) return undefined;
  return sorted[resolvedIdx + 1];
}

/** Chemin public de l'étape suivante — ne saute jamais le checkout s'il est l'étape suivante dans l'ordre. */
export function getNextFunnelStepPath(
  funnel: Pick<SalesFunnel, 'slug' | 'steps'>,
  currentStep: FunnelStep,
): string {
  const sorted = getSortedSteps(funnel as SalesFunnel);
  const next = getNextFunnelStep(funnel, currentStep);
  if (next) return funnelStepPublicPath(funnel.slug, next);

  if (currentStep.type === 'checkout') {
    const thankYou = sorted.find((s) => s.type === 'thank_you');
    if (thankYou) return funnelStepPublicPath(funnel.slug, thankYou);
  }

  if (currentStep.type === 'thank_you') {
    return funnelStepPublicPath(funnel.slug, currentStep);
  }

  const checkout = sorted.find((s) => s.type === 'checkout');
  if (checkout) return funnelStepPublicPath(funnel.slug, checkout);
  return funnelStepPublicPath(funnel.slug, 'sales');
}

/** Première étape du tunnel (ordre éditeur) — point d'entrée « Voir le tunnel ». */
export function funnelEntryPublicUrl(funnel: Pick<SalesFunnel, 'slug' | 'steps'>): string {
  const first = getSortedSteps(funnel as SalesFunnel)[0];
  if (!first) return funnelPublicUrl(funnel.slug);
  return funnelStepPublicUrl(funnel.slug, first);
}

export async function trackFunnelEvent(
  slug: string,
  event: FunnelTrackEvent,
  variant?: 'a' | 'b',
): Promise<void> {
  try {
    await api.post(`/public/funnels/${encodeURIComponent(slug)}/track`, { event, variant });
  } catch {
    /* analytics non bloquante */
  }
}

export async function submitFunnelLead(data: {
  funnelSlug: string;
  email: string;
  firstName?: string;
  lastName?: string;
  consentEmail?: boolean;
}): Promise<{ contactId: string }> {
  const resp = await api.post('/lyrraone/public/leads', data);
  return unwrapApiData<{ contactId: string }>(resp);
}

export function parseCaptureContent(step?: FunnelStep): FunnelCaptureContent {
  if (!step?.content || typeof step.content !== 'object') return {};
  return step.content as FunnelCaptureContent;
}

export function getStepByType(funnel: SalesFunnel, type: FunnelStepType): FunnelStep | undefined {
  return funnel.steps.find((s) => s.type === type);
}

export const FUNNEL_STEP_LABELS: Record<FunnelStepType, string> = {
  capture: 'Inscription',
  sales: 'Offre',
  checkout: 'Paiement',
  thank_you: 'Confirmation',
  custom: 'Étape',
};

/** Étape précédente dans l'ordre éditeur (étapes capture / vente / checkout / merci). */
export function getPreviousFunnelStep(
  funnel: Pick<SalesFunnel, 'steps'>,
  current: Pick<FunnelStep, 'id' | 'type' | 'slug' | 'order'>,
): FunnelStep | undefined {
  const sorted = getSortedSteps(funnel as SalesFunnel);
  const idx = sorted.findIndex((s) => s.id === current.id
    || (s.type === current.type && s.slug === current.slug));
  if (idx <= 0) return undefined;
  return sorted[idx - 1];
}

export function getPreviousFunnelStepPath(
  funnel: Pick<SalesFunnel, 'slug' | 'steps'>,
  current: Pick<FunnelStep, 'id' | 'type' | 'slug' | 'order'>,
): string | null {
  const prev = getPreviousFunnelStep(funnel, current);
  if (!prev) return null;
  return funnelStepPublicPath(funnel.slug, prev);
}

export function getStepBySlug(funnel: SalesFunnel, stepSlug: string): FunnelStep | undefined {
  return funnel.steps.find((s) => s.slug === stepSlug);
}

export function parseSalesContent(step?: FunnelStep): FunnelSalesContent {
  if (!step?.content || typeof step.content !== 'object') return {};
  return step.content as FunnelSalesContent;
}
